// src/pages/UsersAdmin.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";

const ADMIN_EMAIL = "lawilkins13@gmail.com";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString();
};

const UsersAdmin = () => {
  const { user, api } = useAuth();

  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [feedback, setFeedback] = useState([]);

  const [activeTab, setActiveTab] = useState("users");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingFeedbackId, setUpdatingFeedbackId] = useState(null);

  const currentEmail = (user?.email || "").toLowerCase();
  const isAdmin = currentEmail === ADMIN_EMAIL;

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setError("You must be signed in to view this page.");
      return;
    }

    if (!isAdmin) {
      setLoading(false);
      setError(
        "This page is restricted to the FlowQuant founder account."
      );
      return;
    }

    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const [usersRes, eventsRes, updatesRes, feedbackRes] =
          await Promise.all([
            api("/api/auth/users", {
              method: "GET",
            }),
            api("/api/events", {
              method: "GET",
            }),
            api("/api/updates", {
              method: "GET",
            }),
            api("/api/feedback/admin", {
              method: "GET",
            }),
          ]);

        const userRows = Array.isArray(usersRes?.users)
          ? usersRes.users
          : [];

        const eventRows = Array.isArray(eventsRes)
          ? eventsRes
          : [];

        const updateRows = Array.isArray(updatesRes)
          ? updatesRes
          : [];

        const feedbackRows = Array.isArray(feedbackRes?.feedback)
          ? feedbackRes.feedback
          : [];

        if (mounted) {
          setUsers(userRows);
          setEvents(eventRows);
          setUpdates(updateRows);
          setFeedback(feedbackRows);
        }
      } catch (err) {
        console.error(
          "[UsersAdmin] Failed to load admin data:",
          err
        );

        if (mounted) {
          setUsers([]);
          setEvents([]);
          setUpdates([]);
          setFeedback([]);
          setError("Failed to load admin data.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [api, user, isAdmin]);

  const totalSignups = useMemo(() => users.length, [users]);

  const totalWaitlist = useMemo(() => updates.length, [updates]);

  const totalEvents = useMemo(() => events.length, [events]);

  const totalFeedback = useMemo(() => feedback.length, [feedback]);

  const feedbackThisWeek = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;

    return feedback.filter((item) => {
      const createdAt = new Date(item.created_at).getTime();

      return !Number.isNaN(createdAt) && createdAt >= sevenDaysAgo;
    }).length;
  }, [feedback]);

  const registeredFeedback = useMemo(
    () =>
      feedback.filter((item) => item.owner_type === "user").length,
    [feedback]
  );

  const guestFeedback = useMemo(
    () =>
      feedback.filter((item) => item.owner_type === "guest").length,
    [feedback]
  );

  const eventSummary = useMemo(() => {
    const counts = {};

    events.forEach((event) => {
      const key = event.event_name || "unknown_event";

      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([eventName, count]) => ({
        eventName,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [events]);

  const analytics = useMemo(() => {
    const getMetadata = (event) => {
      if (!event?.metadata) return {};

      if (typeof event.metadata === "object") {
        return event.metadata;
      }

      try {
        return JSON.parse(event.metadata);
      } catch {
        return {};
      }
    };

    const uniqueValues = (values) =>
      new Set(values.filter(Boolean)).size;

    const uniqueVisitors = uniqueValues(
      events.map((event) => {
        const metadata = getMetadata(event);

        return metadata.visitor_id || event.owner_id;
      })
    );

    const uniqueSessions = uniqueValues(
      events.map((event) => getMetadata(event).session_id)
    );

    const riskEvents = events.filter(
      (event) => event.module === "risk_calculator"
    );

    const riskCount = (eventName) =>
      riskEvents.filter(
        (event) => event.event_name === eventName
      ).length;

    const riskPageViews = riskCount("risk_page_viewed");
    const riskStarted = riskCount("risk_calculation_started");
    const riskCompleted = riskCount("risk_calculation_completed");
    const riskSaveAttempts = riskCount("risk_save_attempted");
    const riskSaved = riskCount("risk_saved");

    const riskStartedEvents = riskEvents.filter(
      (event) => event.event_name === "risk_calculation_started"
    );

    const guestRiskStarts = riskStartedEvents.filter((event) => {
      const metadata = getMetadata(event);

      return metadata.access_type === "guest";
    }).length;

    const authenticatedRiskStarts = riskStartedEvents.filter(
      (event) => {
        const metadata = getMetadata(event);

        return metadata.access_type === "authenticated";
      }
    ).length;

    const completionRate =
      riskStarted > 0
        ? Math.round((riskCompleted / riskStarted) * 100)
        : 0;

    const saveConversionRate =
      riskStarted > 0
        ? Math.round((riskSaved / riskStarted) * 100)
        : 0;

    const saveSuccessRate =
      riskSaveAttempts > 0
        ? Math.round((riskSaved / riskSaveAttempts) * 100)
        : 0;

    const acquisitionCounts = {};

    events.forEach((event) => {
      const metadata = getMetadata(event);
      const source = metadata.utm_source;

      if (!source) return;

      acquisitionCounts[source] =
        (acquisitionCounts[source] || 0) + 1;
    });

    const acquisitionSources = Object.entries(acquisitionCounts)
      .map(([source, count]) => ({
        source,
        count,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      uniqueVisitors,
      uniqueSessions,
      riskPageViews,
      riskStarted,
      riskCompleted,
      riskSaveAttempts,
      riskSaved,
      guestRiskStarts,
      authenticatedRiskStarts,
      completionRate,
      saveConversionRate,
      saveSuccessRate,
      acquisitionSources,
    };
  }, [events]);

  const feedbackToolSummary = useMemo(() => {
    const counts = {};

    feedback.forEach((item) => {
      const tools = Array.isArray(item.tools)
        ? item.tools
        : [];

      tools.forEach((tool) => {
        counts[tool] = (counts[tool] || 0) + 1;
      });
    });

    return Object.entries(counts)
      .map(([tool, count]) => ({
        tool,
        count,
      }))
      .sort(
        (a, b) =>
          b.count - a.count || a.tool.localeCompare(b.tool)
      );
  }, [feedback]);

  const handleFeedbackStatusChange = async (id, status) => {
    try {
      setUpdatingFeedbackId(id);
      setError("");

      const response = await api(
        `/api/feedback/admin/${id}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({
            status,
          }),
        }
      );

      if (response?.feedback) {
        setFeedback((current) =>
          current.map((item) =>
            item.id === id
              ? {
                  ...item,
                  ...response.feedback,
                }
              : item
          )
        );
      }
    } catch (err) {
      console.error(
        "[UsersAdmin] Failed to update feedback status:",
        err
      );

      setError("Failed to update feedback status.");
    } finally {
      setUpdatingFeedbackId(null);
    }
  };

  const tabs = [
    {
      id: "users",
      label: "Users",
    },
    {
      id: "waitlist",
      label: "Waitlist Signups",
    },
    {
      id: "events",
      label: "Click Events",
    },
    {
      id: "feedback",
      label: "Feedback",
    },
  ];

  if (loading) {
    return (
      <div className="p-6 text-white">
        <div className="mx-auto max-w-6xl rounded-2xl border border-white/10 bg-[#171717] p-8 shadow-lg">
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-blue-400">
            Founder Access
          </p>

          <h2 className="mb-3 text-2xl font-semibold">
            Users Admin
          </h2>

          <p className="text-sm text-gray-400">
            Loading admin data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-white">
        <div className="mx-auto max-w-4xl rounded-2xl border border-white/10 bg-[#171717] p-8 text-center shadow-lg">
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-blue-400">
            Restricted Access
          </p>

          <h2 className="mb-4 text-2xl font-semibold">
            Users Admin
          </h2>

          <p className="text-sm leading-8 text-gray-400 md:text-base">
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 text-white">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* HEADER */}

        <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg md:p-8">
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-blue-400">
            Founder Access
          </p>

          <h2 className="mb-3 text-2xl font-semibold">
            Users Admin
          </h2>

          <p className="max-w-3xl text-sm leading-8 text-gray-400 md:text-base">
            Internal founder-only visibility into FlowQuant growth,
            user activity, waitlist leads, product engagement, and
            customer feedback.
          </p>
        </div>

        {/* GLOBAL KPIS */}

        <div className="grid gap-4 md:grid-cols-5">
          <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
            <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
              Registered Users
            </p>

            <p className="text-3xl font-semibold text-white">
              {totalSignups}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
            <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
              Founder Email
            </p>

            <p className="break-all text-sm font-medium text-white md:text-base">
              {currentEmail || "-"}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
            <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
              Waitlist Leads
            </p>

            <p className="text-3xl font-semibold text-white">
              {totalWaitlist}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
            <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
              Click Events
            </p>

            <p className="text-3xl font-semibold text-white">
              {totalEvents}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
            <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
              Feedback
            </p>

            <p className="text-3xl font-semibold text-white">
              {totalFeedback}
            </p>
          </div>
        </div>

        {/* TABS */}

        <div className="rounded-2xl border border-white/10 bg-[#171717] p-2 shadow-lg">
          <div className="flex flex-col gap-2 sm:flex-row">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={[
                  "rounded-xl px-4 py-2 text-sm font-medium transition-colors",
                  activeTab === tab.id
                    ? "bg-blue-600 text-white"
                    : "text-gray-300 hover:bg-[#202020] hover:text-white",
                ].join(" ")}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* USERS */}

        {activeTab === "users" && (
          <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg">
            <div className="mb-4">
              <p className="mb-2 text-xs uppercase tracking-[0.2em] text-blue-400">
                Signup Records
              </p>

              <h3 className="text-xl font-semibold">
                Registered Users
              </h3>
            </div>

            {users.length === 0 ? (
              <p className="text-sm text-gray-400">
                No real signups found yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-white/10 text-gray-400">
                    <tr>
                      <th className="px-4 py-3 font-medium">
                        Email
                      </th>
                      <th className="px-4 py-3 font-medium">
                        Username
                      </th>
                      <th className="px-4 py-3 font-medium">
                        First Name
                      </th>
                      <th className="px-4 py-3 font-medium">
                        Last Name
                      </th>
                      <th className="px-4 py-3 font-medium">
                        Signed Up
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {users.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-white/5 text-gray-200"
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          {row.email || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {row.username || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {row.first_name || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {row.last_name || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-gray-400">
                          {formatDate(row.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* WAITLIST */}

        {activeTab === "waitlist" && (
          <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg">
            <div className="mb-4">
              <p className="mb-2 text-xs uppercase tracking-[0.2em] text-blue-400">
                Updates List
              </p>

              <h3 className="text-xl font-semibold">
                Waitlist Signups
              </h3>
            </div>

            {updates.length === 0 ? (
              <p className="text-sm text-gray-400">
                No waitlist signups found yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-white/10 text-gray-400">
                    <tr>
                      <th className="px-4 py-3 font-medium">
                        Name
                      </th>
                      <th className="px-4 py-3 font-medium">
                        Email
                      </th>
                      <th className="px-4 py-3 font-medium">
                        Trader Type
                      </th>
                      <th className="px-4 py-3 font-medium">
                        Joined
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {updates.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-white/5 text-gray-200"
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          {row.name || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {row.email || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {row.trader_type || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-gray-400">
                          {formatDate(row.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* CLICK EVENTS / ANALYTICS V2 */}

        {activeTab === "events" && (
          <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg">
            <div className="mb-4">
              <p className="mb-2 text-xs uppercase tracking-[0.2em] text-blue-400">
                User Behavior
              </p>

              <h3 className="text-xl font-semibold">
                Click Events
              </h3>
            </div>

            {/* ANALYTICS KPIS */}

            <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border border-white/10 bg-[#111111] p-4">
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
                  Unique Visitors
                </p>

                <p className="text-3xl font-semibold text-white">
                  {analytics.uniqueVisitors}
                </p>

                <p className="mt-2 text-xs text-gray-500">
                  Distinct tracked browsers
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-[#111111] p-4">
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
                  Sessions
                </p>

                <p className="text-3xl font-semibold text-white">
                  {analytics.uniqueSessions}
                </p>

                <p className="mt-2 text-xs text-gray-500">
                  Analytics V2 sessions
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-[#111111] p-4">
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
                  Risk Starts
                </p>

                <p className="text-3xl font-semibold text-white">
                  {analytics.riskStarted}
                </p>

                <p className="mt-2 text-xs text-gray-500">
                  Calculator attempts
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-[#111111] p-4">
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
                  Risk Saves
                </p>

                <p className="text-3xl font-semibold text-white">
                  {analytics.riskSaved}
                </p>

                <p className="mt-2 text-xs text-gray-500">
                  Persisted calculations
                </p>
              </div>
            </div>

            {/* RISK FUNNEL */}

            <div className="mb-6 rounded-xl border border-white/10 bg-[#111111] p-5">
              <div className="mb-5">
                <p className="mb-2 text-xs uppercase tracking-[0.2em] text-blue-400">
                  Product Funnel
                </p>

                <h4 className="text-lg font-semibold text-white">
                  Risk Calculator Funnel
                </h4>

                <p className="mt-2 text-sm text-gray-500">
                  Current event volume across the Risk Calculator journey.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                {[
                  ["Page Views", analytics.riskPageViews],
                  ["Started", analytics.riskStarted],
                  ["Completed", analytics.riskCompleted],
                  ["Save Attempts", analytics.riskSaveAttempts],
                  ["Saved", analytics.riskSaved],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-xl border border-white/10 bg-[#15171b] p-4"
                  >
                    <p className="text-xs uppercase tracking-[0.16em] text-gray-500">
                      {label}
                    </p>

                    <p className="mt-2 text-2xl font-semibold text-white">
                      {value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <div className="rounded-xl border border-white/10 bg-[#15171b] p-4">
                  <p className="text-xs uppercase tracking-[0.16em] text-gray-500">
                    Completion Rate
                  </p>

                  <p className="mt-2 text-2xl font-semibold text-white">
                    {analytics.completionRate}%
                  </p>
                </div>

                <div className="rounded-xl border border-white/10 bg-[#15171b] p-4">
                  <p className="text-xs uppercase tracking-[0.16em] text-gray-500">
                    Save Conversion
                  </p>

                  <p className="mt-2 text-2xl font-semibold text-white">
                    {analytics.saveConversionRate}%
                  </p>
                </div>

                <div className="rounded-xl border border-white/10 bg-[#15171b] p-4">
                  <p className="text-xs uppercase tracking-[0.16em] text-gray-500">
                    Save Success
                  </p>

                  <p className="mt-2 text-2xl font-semibold text-white">
                    {analytics.saveSuccessRate}%
                  </p>
                </div>

                <div className="rounded-xl border border-white/10 bg-[#15171b] p-4">
                  <p className="text-xs uppercase tracking-[0.16em] text-gray-500">
                    Guest Starts
                  </p>

                  <p className="mt-2 text-2xl font-semibold text-white">
                    {analytics.guestRiskStarts}
                  </p>
                </div>

                <div className="rounded-xl border border-white/10 bg-[#15171b] p-4">
                  <p className="text-xs uppercase tracking-[0.16em] text-gray-500">
                    Registered Starts
                  </p>

                  <p className="mt-2 text-2xl font-semibold text-white">
                    {analytics.authenticatedRiskStarts}
                  </p>
                </div>
              </div>
            </div>

            {/* ACQUISITION */}

            <div className="mb-6 rounded-xl border border-white/10 bg-[#111111] p-5">
              <div className="mb-4">
                <p className="mb-2 text-xs uppercase tracking-[0.2em] text-blue-400">
                  Acquisition
                </p>

                <h4 className="text-lg font-semibold text-white">
                  Tracked Sources
                </h4>
              </div>

              {analytics.acquisitionSources.length === 0 ? (
                <p className="text-sm text-gray-400">
                  No UTM-tagged traffic has been captured by Analytics V2 yet.
                </p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {analytics.acquisitionSources.map((item) => (
                    <div
                      key={item.source}
                      className="rounded-xl border border-white/10 bg-[#15171b] p-4"
                    >
                      <p className="text-sm text-gray-300">
                        {item.source}
                      </p>

                      <p className="mt-2 text-2xl font-semibold text-white">
                        {item.count}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* EVENT SUMMARY */}

            {eventSummary.length > 0 && (
              <div className="mb-6 rounded-xl border border-white/10 bg-[#111111] p-4">
                <p className="mb-3 text-xs uppercase tracking-[0.2em] text-blue-400">
                  Event Summary
                </p>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b border-white/10 text-gray-400">
                      <tr>
                        <th className="px-4 py-3 font-medium">
                          Event
                        </th>

                        <th className="px-4 py-3 font-medium">
                          Clicks
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {eventSummary.map((row) => (
                        <tr
                          key={row.eventName}
                          className="border-b border-white/5 text-gray-200"
                        >
                          <td className="px-4 py-3">
                            {row.eventName}
                          </td>

                          <td className="px-4 py-3 font-semibold">
                            {row.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* RAW EVENTS */}

            {events.length === 0 ? (
              <p className="text-sm text-gray-400">
                No click events found yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-white/10 text-gray-400">
                    <tr>
                      <th className="px-4 py-3 font-medium">
                        Event
                      </th>

                      <th className="px-4 py-3 font-medium">
                        Module
                      </th>

                      <th className="px-4 py-3 font-medium">
                        Owner Type
                      </th>

                      <th className="px-4 py-3 font-medium">
                        Owner ID
                      </th>

                      <th className="px-4 py-3 font-medium">
                        Path
                      </th>

                      <th className="px-4 py-3 font-medium">
                        Created
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {events.map((event) => (
                      <tr
                        key={event.id}
                        className="border-b border-white/5 text-gray-200"
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          {event.event_name || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {event.module || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {event.owner_type || "-"}
                        </td>

                        <td className="max-w-[220px] truncate px-4 py-3 text-gray-400">
                          {event.owner_id || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-gray-400">
                          {event.path || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-gray-400">
                          {formatDate(event.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* FEEDBACK */}

        {activeTab === "feedback" && (
          <div className="space-y-6">

            {/* FEEDBACK KPIS */}

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
                  Total Feedback
                </p>

                <p className="text-3xl font-semibold text-white">
                  {totalFeedback}
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
                  This Week
                </p>

                <p className="text-3xl font-semibold text-white">
                  {feedbackThisWeek}
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
                  Registered Users
                </p>

                <p className="text-3xl font-semibold text-white">
                  {registeredFeedback}
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-blue-400">
                  Guests
                </p>

                <p className="text-3xl font-semibold text-white">
                  {guestFeedback}
                </p>
              </div>
            </div>

            {/* REQUESTED AREAS */}

            <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg">
              <div className="mb-4">
                <p className="mb-2 text-xs uppercase tracking-[0.2em] text-blue-400">
                  Product Signals
                </p>

                <h3 className="text-xl font-semibold">
                  Most Requested Areas
                </h3>
              </div>

              {feedbackToolSummary.length === 0 ? (
                <p className="text-sm text-gray-400">
                  No tool requests found yet.
                </p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {feedbackToolSummary.map((item) => (
                    <div
                      key={item.tool}
                      className="rounded-xl border border-white/10 bg-[#111111] p-4"
                    >
                      <p className="text-sm text-gray-300">
                        {item.tool}
                      </p>

                      <p className="mt-2 text-2xl font-semibold text-white">
                        {item.count}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* RECENT FEEDBACK */}

            <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg">
              <div className="mb-4">
                <p className="mb-2 text-xs uppercase tracking-[0.2em] text-blue-400">
                  Product Feedback
                </p>

                <h3 className="text-xl font-semibold">
                  Recent Feedback
                </h3>
              </div>

              {feedback.length === 0 ? (
                <p className="text-sm text-gray-400">
                  No feedback found yet.
                </p>
              ) : (
                <div className="space-y-4">
                  {feedback.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-xl border border-white/10 bg-[#111111] p-5"
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-3 flex flex-wrap gap-2">
                            {(
                              Array.isArray(item.tools)
                                ? item.tools
                                : []
                            ).map((tool) => (
                              <span
                                key={`${item.id}-${tool}`}
                                className="rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-xs font-medium text-blue-300"
                              >
                                {tool}
                              </span>
                            ))}
                          </div>

                          <p className="whitespace-pre-wrap text-sm leading-7 text-gray-200">
                            {item.feedback_text || "-"}
                          </p>

                          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-500">
                            <span>
                              Owner:{" "}
                              {item.owner_type === "user"
                                ? "Registered User"
                                : "Guest"}
                            </span>

                            <span>
                              {formatDate(item.created_at)}
                            </span>

                            <span className="max-w-[260px] truncate">
                              ID: {item.owner_id || "-"}
                            </span>
                          </div>
                        </div>

                        <div className="w-full shrink-0 lg:w-44">
                          <label
                            htmlFor={`feedback-status-${item.id}`}
                            className="mb-2 block text-xs uppercase tracking-[0.16em] text-gray-500"
                          >
                            Status
                          </label>

                          <select
                            id={`feedback-status-${item.id}`}
                            value={item.status || "new"}
                            disabled={
                              updatingFeedbackId === item.id
                            }
                            onChange={(event) =>
                              handleFeedbackStatusChange(
                                item.id,
                                event.target.value
                              )
                            }
                            className="w-full rounded-xl border border-[#303645] bg-[#0b0d12] px-3 py-2 text-sm text-gray-200 outline-none transition focus:border-blue-500 disabled:cursor-wait disabled:opacity-60"
                          >
                            <option value="new">
                              New
                            </option>

                            <option value="reviewing">
                              Reviewing
                            </option>

                            <option value="planned">
                              Planned
                            </option>

                            <option value="shipped">
                              Shipped
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersAdmin;